import { useState } from 'react';
import { Plane, Check, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import COUNTRIES from '@/data/countries';

const VisaServicePage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    visa_type: '',
    destination_country: '',
    nationality: '',
    passport_number: '',
    travel_purpose: '',
    intended_travel_date: '',
    duration_of_stay: '',
    previous_visa_rejections: 'no',
    additional_info: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/service-requests', {
        ...formData,
        service_category: 'visa_service',
        created_at: new Date().toISOString(),
      });
      
      toast.success('درخواست اخذ ویزای شما ثبت شد. کارشناسان ما به زودی با شما تماس می‌گیرند.');
      
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        visa_type: '',
        destination_country: '',
        nationality: '',
        passport_number: '',
        travel_purpose: '',
        intended_travel_date: '',
        duration_of_stay: '',
        previous_visa_rejections: 'no',
        additional_info: '',
      });
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isDark ? 'bg-[#D4AF37]/10' : 'bg-[#D4AF37]/20'
          }`}>
            <Plane className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className={`text-4xl sm:text-5xl font-light mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            اخذ ویزا
          </h1>
          <p className={`text-lg ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            راهنمایی کامل و پیگیری اخذ ویزای تحصیلی، کاری و توریستی
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { title: 'بررسی مدارک', desc: 'بررسی دقیق مدارک قبل از ارسال' },
            { title: 'تکمیل فرم‌ها', desc: 'تکمیل صحیح فرم‌های ویزا' },
            { title: 'آماده‌سازی مصاحبه', desc: 'آموزش و آماده‌سازی برای مصاحبه' },
          ].map((item) => (
            <div key={item.title} className={`border rounded-xl p-6 text-center ${
              isDark 
                ? 'glass-effect border-white/10' 
                : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <FileCheck className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className={`font-medium mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{item.title}</h3>
              <p className={`text-sm ${
                isDark ? 'text-zinc-400' : 'text-gray-600'
              }`}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className={`border rounded-2xl p-8 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h2 className={`text-2xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>فرم درخواست اخذ ویزا</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>نام و نام خانوادگی *</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>شماره تماس *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>ایمیل *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>نوع ویزا *</label>
              <select
                name="visa_type"
                required
                value={formData.visa_type}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                    : 'bg-white border-gray-300 text-gray-900'
                } font-medium`}
              >
                <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                <option value="student" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>تحصیلی (Student Visa)</option>
                <option value="work" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>کاری (Work Visa)</option>
                <option value="tourist" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>توریستی (Tourist Visa)</option>
                <option value="business" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>تجاری (Business Visa)</option>
                <option value="family" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>خانوادگی (Family Visa)</option>
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>کشور مقصد *</label>
                <select
                  name="destination_country"
                  required
                  value={formData.destination_country}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } font-medium`}
                >
                  <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country} className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>ملیت *</label>
                <select
                  name="nationality"
                  required
                  value={formData.nationality}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                    isDark 
                      ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } font-medium`}
                >
                  <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country} className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>شماره پاسپورت</label>
              <input
                type="text"
                name="passport_number"
                placeholder="اختیاری"
                value={formData.passport_number}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>هدف از سفر *</label>
              <textarea
                name="travel_purpose"
                required
                rows="3"
                placeholder="مثلاً: تحصیل در دانشگاه، کار در شرکت، گردشگری و..."
                value={formData.travel_purpose}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>تاریخ سفر موردنظر</label>
                <input
                  type="text"
                  name="intended_travel_date"
                  placeholder="مثلاً: شهریور 1405"
                  value={formData.intended_travel_date}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>مدت اقامت</label>
                <input
                  type="text"
                  name="duration_of_stay"
                  placeholder="مثلاً: 2 سال، 6 ماه"
                  value={formData.duration_of_stay}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>آیا قبلاً ویزایی رد شده است? *</label>
              <select
                name="previous_visa_rejections"
                required
                value={formData.previous_visa_rejections}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]' 
                    : 'bg-white border-gray-300 text-gray-900'
                } font-medium`}
              >
                <option value="no" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>خیر</option>
                <option value="yes" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>بله</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>توضیحات تکمیلی</label>
              <textarea
                name="additional_info"
                rows="4"
                placeholder="اطلاعات دیگری که فکر می‌کنید مهم است..."
                value={formData.additional_info}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium py-4 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'در حال ارسال...' : 'ارسال درخواست ویزا'}
            </button>
          </form>
        </div>

        {/* Success Rate Info */}
        <div className={`mt-8 border rounded-xl p-6 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <h3 className={`font-medium mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>چرا خدمات ویزای ما؟</h3>
          <ul className={`space-y-3 ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>نرخ موفقیت بالای ۸۵٪ در اخذ ویزا</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>تیم مجرب با سال‌ها تجربه در امور ویزا</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>پشتیبانی کامل از ابتدا تا دریافت ویزا</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span>آماده‌سازی حرفه‌ای برای مصاحبه سفارت</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VisaServicePage;
